'use client';

import * as React from 'react';

import { ArrowLeft20Filled, ArrowRight20Filled } from '@fluentui/react-icons';
import { format } from 'date-fns';
import type {
  CaptionProps,
  DateFormatter,
  StyledElement,
} from 'react-day-picker';
import { DayPicker, useNavigation } from 'react-day-picker';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  onTodayClick?: () => void;
  showActions?: boolean;
  leftArrowTestId?: string;
  rightArrowTestId?: string;
};

const getCalendarStyles = (
  classNames: Partial<StyledElement<string>> | undefined,
) => ({
  months:
    'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 bg-white rounded',
  month: 'space-y-4 p-2',
  caption: 'flex justify-center pt-1 relative items-center mb-2',
  caption_label: 'text-sm font-medium',
  nav: 'space-x-1 flex items-center',
  nav_button:
    'h-7 w-7 bg-transparent p-0 opacity-50 border border-gray-200 rounded-xs inline-flex items-center justify-center',
  nav_button_previous: 'absolute left-1',
  nav_button_next: 'absolute right-1',
  table: 'w-full border-collapse space-y-1 bg-white rounded',
  head_row: 'flex',
  head_cell: 'text-gray-400 rounded-md w-12 font-normal text-body-4',
  row: 'flex w-full',
  cell: 'text-center text-gray-900 text-body-4 p-0 relative !rounded-full focus-within:relative focus-within:z-20 mx-2 my-1',
  day: 'inline-flex h-8 w-8 !rounded-full items-center justify-center rounded-xs transition-colors hover:bg-primary-100 disabled:opacity-50 disabled:pointer-events-none',
  day_selected:
    'border border-primary text-primary hover:bg-primary-100 !rounded-full',
  day_today:
    '!text-base-white bg-primary-500 !rounded-full hover:bg-primary-600',
  day_outside: 'text-gray-400',
  day_range_middle: 'aria-selected:bg-primary aria-selected:text-white',
  day_hidden: 'invisible',
  ...classNames,
});

export const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  onTodayClick,
  showActions = true,
  ...props
}: CalendarProps) => {
  const [month, setMonth] = React.useState<Date>(() => new Date());

  React.useEffect(() => {
    if (props.selected && props.mode === 'single') {
      setMonth(props.selected);
    }
  }, [props.mode, props.selected]);

  const formatWeekdayNames: DateFormatter = (date, options) => (
    <>{format(new Date(date), 'EEE', { locale: options?.locale }).charAt(0)}</>
  );

  function CustomCaption(captionProps: CaptionProps) {
    const { goToMonth, nextMonth, previousMonth } = useNavigation();

    if (!showActions) {
      return null;
    }

    return (
      <div className="flex items-center justify-between">
        <div className="text-primary flex gap-[0.625rem]">
          <button
            className="flex items-center p-[0.625rem]"
            disabled={!previousMonth}
            onClick={() => {
              if (previousMonth) goToMonth(previousMonth);
            }}
            data-testid={props.leftArrowTestId}
          >
            <ArrowLeft20Filled />
          </button>
          <span className="text-base-black flex items-center px-1 text-[1rem] font-semibold leading-[1rem]">
            {format(captionProps.displayMonth, 'MMMM yyy')}
          </span>
          <button
            className="flex items-center p-[0.625rem]"
            disabled={!nextMonth}
            onClick={() => {
              if (nextMonth) goToMonth(nextMonth);
            }}
            data-testid={props.rightArrowTestId}
          >
            <ArrowRight20Filled />
          </button>
        </div>
        <button
          className="text-primary text-body-2 hover:text-primary-600 px-4 py-2"
          onClick={() => {
            setMonth(new Date());
            onTodayClick?.();
          }}
        >
          Today
        </button>
      </div>
    );
  }

  return (
    <DayPicker
      month={month}
      today={new Date()}
      onMonthChange={setMonth}
      showOutsideDays={showOutsideDays}
      formatters={{
        formatWeekdayName: formatWeekdayNames,
      }}
      components={{
        Caption: CustomCaption,
      }}
      className={className}
      classNames={getCalendarStyles(classNames)}
      {...props}
    />
  );
};
Calendar.displayName = 'Calendar';
